import moment from 'moment';
import * as yup from 'yup';

const lineTimeWithinCensusTest = (censusStartTime?: string, censusEndTime?: string) =>
  function (this: yup.TestContext, value: string | undefined) {
    if (!value || !censusStartTime || !censusEndTime) {
      return true;
    }
    const lineTime = moment(value, 'HH:mm', true);
    const start = moment(censusStartTime, 'HH:mm', true);
    const end = moment(censusEndTime, 'HH:mm', true);
    if (!lineTime.isValid() || !start.isValid() || !end.isValid()) {
      return true;
    }
    if (lineTime.isBefore(start)) {
      return this.createError({
        message: 'La hora no puede ser anterior a la hora de inicio del censo',
      });
    }
    if (lineTime.isAfter(end)) {
      return this.createError({
        message: 'La hora no puede ser posterior a la hora de fin del censo',
      });
    }
    return true;
  };

export const createLineValidationSchema = (
  censusStartTime?: string,
  censusEndTime?: string
) =>
  yup.object({
    numIndividuals: yup.string().required('Campo requerido'),
    aeroStatus: yup.string().required('Campo requerido'),
    altitudLevel: yup.mixed().nullable().required('Campo requerido'),
    distanceToAero: yup.string().nullable().required('Campo requerido'),
    riskDetected: yup.mixed().required('Campo requerido'),
    isOfc: yup.string().required('Campo requerido'),
    dirOrigen: yup.string().required('Campo requerido'),
    dirDest: yup.string().required('Campo requerido'),
    reacc: yup.string().required('Campo requerido'),
    aeroOnComments: yup.string().nullable().test({
      name: 'aeroOnComments',
      test: function (value) {
        const { aeroStatus } = this.parent;
        if (aeroStatus === 'apagado' && !value) {
          return this.createError({
            message: 'Campo requerido cuando el aerogenerador está apagado',
          });
        }
        return true;
      },
    }),
    askStop: yup
      .string()
      .required('Campo requerido')
      .test('askStop-apagado', function (value) {
        const { aeroStatus } = this.parent;
        if (aeroStatus === 'apagado' && value !== 'no') {
          return this.createError({
            message: 'Si el aerogenerador está apagado, se solicita parada debe ser NO',
          });
        }
        return true;
      }),
    specieId: yup.string().required('Campo requerido'),
    windDirection: yup.string().required('Campo requerido'),
    windSpeed: yup.mixed().required('Campo requerido'),
    cloudCoverage: yup.mixed().required('Campo requerido'),
    visibility: yup.string().required('Campo requerido'),
    censusMin: yup
      .mixed()
      .required('Campo requerido')
      .test('non-negative', 'Los minutos del censo no pueden ser negativos', (value) => {
        if (value === undefined || value === null || value === '') {
          return true;
        }
        const minutes = Number(value);
        return !Number.isNaN(minutes) && minutes >= 0;
      }),
    time: yup
      .string()
      .required('Campo requerido')
      .test(
        'within-census',
        'La hora debe estar entre la hora de inicio y la de fin del censo',
        lineTimeWithinCensusTest(censusStartTime, censusEndTime)
      ),
    rain: yup.string().required('Campo requerido'),
    turbineId: yup.string().required('Campo requerido'),
  });

export const lineValidationSchema = createLineValidationSchema();

export const generalDataValidationSchema = yup.object({
  parkId: yup.string().required('Campo requerido'),
  userId: yup.string().required('Campo requerido'),
  year: yup.string().required('Campo requerido'),
  month: yup.string().required('Campo requerido'),
  week: yup.string().required('Campo requerido'),
  date: yup.string().required('Campo requerido'),
  time: yup.string().required('Campo requerido').test({
    name: 'time',
    test: function (value) {
      const { endTime } = this.parent;
      if (value && endTime) {
        const startTime = moment(value, 'HH:mm');
        const endTimeObj = moment(endTime, 'HH:mm');
        if (startTime.isValid() && endTimeObj.isValid()) {
          if (endTimeObj.isBefore(startTime)) {
            return this.createError({
              message: 'La hora de inicio debe ser menor o igual a la hora de fin',
            });
          }
        }
      }
      return true;
    },
  }),
  endTime: yup.string().required('Campo requerido').test({
    name: 'endTime',
    test: function (value) {
      const { time } = this.parent;
      if (time && value) {
        const startTime = moment(time, 'HH:mm');
        const endTime = moment(value, 'HH:mm');
        if (startTime.isValid() && endTime.isValid()) {
          if (endTime.isBefore(startTime)) {
            return this.createError({
              message: 'La hora de fin debe ser mayor o igual a la hora de inicio',
            });
          }
        }
      }
      return true;
    },
  }),
  censusDpma: yup.string().required('Campo requerido'),
  observationPost: yup.string().required('Campo requerido'),
});

export const generalDataValidationSchemaOFC = yup.object({
  parkId: yup.string().required('Campo requerido'),
  userId: yup.string().required('Campo requerido'),
});

export const SuperAdminSchemaOFC = generalDataValidationSchemaOFC.shape({
  company: yup.string().required('Campo requerido'),
});

export const SuperAdminSchema = generalDataValidationSchema.shape({
  company: yup.string().required('Campo requerido'),
});

export interface TabData {
  title: string;
  description: string;
  isLine: boolean;
  isValid: boolean;
  formikValues: any;
  formikErrors: any;
  formikTouched: any;
  lineId?: string;
}
